import { AbstractControl, FormGroup, NgForm, ValidationErrors } from '@angular/forms';

const SameDigitsCPF = [
  '00000000000',
  '11111111111',
  '22222222222',
  '33333333333',
  '44444444444',
  '55555555555',
  '66666666666',
  '77777777777',
  '88888888888',
  '99999999999'
];

const SameDigitsCNPJ = [
  '00000000000000',
  '11111111111111',
  '22222222222222',
  '33333333333333',
  '44444444444444',
  '55555555555555',
  '66666666666666',
  '77777777777777',
  '88888888888888',
  '99999999999999'
];

export class Validators {
  static number(control: AbstractControl): ValidationErrors | null {
    if (isNaN(control.value)) {
      return {not_num: true};
    }
    return null;
  }

  static cpfCnpj(control: AbstractControl): ValidationErrors | null {
    if (!control.value || !control.value.trim()) {
      return null;
    }
    const value = control.value.trim();
    if (value.length === 14) {
      return Validators.cpf(control);
    }
    return Validators.cnpj(control);
  }

  static cpf(control: AbstractControl): ValidationErrors | null {

    if (!control.value || !control.value.trim()) {
      return null;
    }

    let digitosIguais = 1;

    const cpf = control.value.replace(/\D/gi, '');

    if (cpf.length < 11) {
      return {length: true};
    }

    if (SameDigitsCPF.indexOf(cpf) >= 0) {
      return {cpf: true};
    }

    for (let i = 0; i < cpf.length - 1; i++) {
      if (cpf.charAt(i) !== cpf.charAt(i + 1)) {
        digitosIguais = 0;
        break;
      }
    }

    if (digitosIguais) {
      return {cpf: true};
    }

    let numeros = cpf.substring(0, 9);
    let soma = 0;
    const digitos = cpf.substring(9);

    for (let i = 10; i > 1; i--) {
      soma += numeros.charAt(10 - i) * i;
    }

    let resultado = soma % 11 < 2 ? 0 : 11 - soma % 11;

    if (resultado.toString() !== digitos.charAt(0)) {
      return {cpf: true};
    }

    numeros = cpf.substring(0, 10);
    soma = 0;

    for (let i = 11; i > 1; i--) {
      soma += numeros.charAt(11 - i) * i;
    }

    resultado = soma % 11 < 2 ? 0 : 11 - soma % 11;

    if (resultado.toString() !== digitos.charAt(1)) {
      return {cpf: true};
    }

    return null;
  }

  static cnpj(control: AbstractControl): ValidationErrors | null {

    if (!control.value || !control.value.trim()) {
      return null;
    }

    const cnpj = control.value.replace(/\D/gi, '');

    if (cnpj.length < 14) {
      return {length: true};
    }

    // Elimina CNPJs invalidos conhecidos
    if (SameDigitsCNPJ.indexOf(cnpj) >= 0) {
      return {cnpj: true};
    }

    // Valida DVs
    let tamanho = cnpj.length - 2;
    let soma = 0;
    let pos = tamanho - 7;
    let numeros = cnpj.substring(0, tamanho);
    const digitos = cnpj.substring(tamanho);

    for (let i = tamanho; i >= 1; i--) {
      soma += numeros.charAt(tamanho - i) * pos--;
      if (pos < 2) {
        pos = 9;
      }
    }

    let resultado = soma % 11 < 2 ? 0 : 11 - soma % 11;
    if (resultado.toString() !== digitos.charAt(0)) {
      return {cnpj: true};
    }

    tamanho = tamanho + 1;
    numeros = cnpj.substring(0, tamanho);
    soma = 0;
    pos = tamanho - 7;

    for (let i = tamanho; i >= 1; i--) {
      soma += numeros.charAt(tamanho - i) * pos--;
      if (pos < 2) {
        pos = 9;
      }
    }

    resultado = soma % 11 < 2 ? 0 : 11 - soma % 11;

    if (resultado.toString() !== digitos.charAt(1)) {
      return {cpnj: true};
    }

    return null;
  }

  static nome(control: AbstractControl): ValidationErrors | null {

    if (!control.value) {
      return null;
    }
    const nome = control.value;
    if (isNaN((nome).trim().replace(/\s+/g, '')) && nome.length > 1) {
      return null;
    }
    return {nome: false};
  }

  static email(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }
    const email = control.value;
    if ((/.+@\w+\.\w+(\.\w+)?/.test(email))) {
      return null;
    }
    return {email: true};
  }

  static confirmarEmail(control: AbstractControl): ValidationErrors | null {
    if (!control.get('dsEmail').value) {
      return null;
    }
    if (!control.get('dsEmailConfirmacao').value) {
      return null;
    }
    const email = control.get('dsEmail').value;
    const confirmarEmail = control.get('dsEmailConfirmacao').value;
    if (email === confirmarEmail) { return null; }
    control.get('dsEmailConfirmacao').setErrors({confirmarEmail: true});
  }

  static data(control: AbstractControl): ValidationErrors | null {
    if (!control.value || control.value instanceof Date) {
      return null;
    }
    const ptDatePattern = '/^(((0[1-9]|[12]\d|3[01])\/(0[13578]|1[02])\/((19|[2-9]\d)\d{2}))|((0[1-9]|[12]\d|30)\/' +
      '(0[13456789]|1[012])\/((19|[2-9]\d)\d{2}))|((0[1-9]|1\d|2[0-8])\/02\/((19|[2-9]\d)\d{2}))|(29\/02\/' +
      '((1[6-9]|[2-9]\d)(0[48]|[2468][048]|[13579][26])|((16|[2468][048]|[3579][26])00))))$/g';

    if (!control.value.match(ptDatePattern)) {
      return {ptDate: true};
    }

    return null;
  }

  static cep(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    const cep = (control.value).replace('-', '').replace('.', '');
    if (cep.length !== 8 || isNaN(cep)) {
      return {invalid_cep: true};
    }
    return null;
  }

  static municipio(control: AbstractControl): ValidationErrors | null {
    if (!control.value || typeof control.value === 'object') {
      return null;
    }
    return {invalid_municipio: true};
  }

  static uf(control: AbstractControl): ValidationErrors | null {
    if (!control.value || typeof control.value === 'object') {
      return null;
    }
    return {invalid_uf: true};
  }

  static tel(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }
    const tel = (control.value).replace('-', '').replace('(', '').replace(')', '').replace(' ', '');
    if (tel.length < 10 || (tel.length >= 11) && isNaN(tel)) {
      return {invalid_tel: true};
    }
    return null;
  }

  static required(control: AbstractControl): ValidationErrors | null {
    if (!(control.value + '').trim().length) {
      return {required: true};
    }
    return null;
  }

  static lattesId(control: AbstractControl): ValidationErrors | null {
    if (control.value && !/^https?:\/\/lattes.cnpq.br\/[1-9]+$/g.test(control.value)) {
      return {lattes: true};
    }
    return null;
  }

  static url(control: AbstractControl): ValidationErrors | null {
    const pattern = new RegExp('^(https?:\\/\\/)?' +
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' +
      '((\\d{1,3}\\.){3}\\d{1,3}))' +
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' +
      '(\\?[;&a-z\\d%_.~+=-]*)?' +
      '(\\#[-a-z\\d_]*)?$', 'i');
    if (control.value && !pattern.test(control.value)) {
      return {url: true};
    }
    return null;
  }

  static requiredHtml(control: AbstractControl): ValidationErrors | null {
    if ((control.value !== false && !control.value) || !(control.value + '').replace(/<[^>]*>?/gm, '').trim().length) {
      return {required_html: true};
    }
    return null;
  }

  static hourValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }
    const hour = control.value.split(':');
    // tslint:disable-next-line:radix
    hour[0] = parseInt(hour[0]);
    // tslint:disable-next-line:radix
    hour[0] = parseInt(hour[0]);
    if (isNaN(hour[0]) || isNaN(hour[1]) || hour[0] < 0 || hour[0] > 23 || hour[1] < 0 || hour[1] > 59) {
      return {invalid_hour: true};
    }
    return null;
  }

  static updateFormControl(formControl, keys ?: Array<string>) {
    const controls = Object.keys(formControl);
    for (const control of controls) {
      const formCtrl = formControl[control];
      if (formCtrl instanceof FormGroup) {
        this.updateFormControl(formCtrl.controls, keys);
        continue;
      }
      if (keys) {
        const hasKey = keys.some(
          x => x === control);
        if (hasKey) {
          formCtrl.updateValueAndValidity();
        }
      } else {
        formCtrl.updateValueAndValidity();
      }
    }
  }

  static formularioValido(form: FormGroup,
                          keys?: Array<string>) {
    this.updateFormControl(form.controls, keys);
    if (!form.valid && !keys) {
      return false;
    }
    return true;
  }

  // tslint:disable-next-line:ban-types
  static setarValorForm(formControl: FormGroup, object: Object) {
    if (object && typeof object === 'object') {
      const keys = Object.keys(object);
      for (const key of keys) {
        if (formControl && formControl.get(key)) {
          if (formControl.get(key) instanceof FormGroup && typeof object[key] === 'object') {
            this.setarValorForm(formControl.get(key) as FormGroup, object[key]);
            continue;
          }
          formControl.get(key).setValue(object[key]);
        }
      }
    }
  }

  static markFormPristine(form: FormGroup | NgForm): void {
    Object.keys(form.controls).forEach(control => {
      form.controls[control].clearValidators();
      form.controls[control].setValue('');
      form.controls[control].markAsPristine();
    });
  }

}